import React, { useState } from 'react';
import Link from 'next/link';
import { FiBarChart } from 'react-icons/fi';
import useTranslation from 'next-translate/useTranslation';
import { MdOutlinePlayArrow } from 'react-icons/md';
import { BiDollar, BiShareAlt } from 'react-icons/bi';
import { Play, DollarSign } from 'react-feather';
import styled from 'styled-components';
import {
  Track,
  TrackArtist,
  TrackRelease,
  style,
  useIsCurrentUser,
} from '../../api';
import {
  TextBold14,
  TextBold22,
  TextBolder14,
  TextRegular18,
} from '../../VuexyDesign/atoms/Text';
import {
  Button,
  Col,
  ProOutlineButton,
  Row,
} from '../../VuexyDesign/atoms/_index';
import { DistributionIconGroup } from '../../VuexyDesign/molecules/_index';
import StatusForTrackDetailPage from './StatusForTrackDetailPage';
import GetTrackInfoOwnerData from './GetTrackInfoOwnerData';

const ReleasedTrackInfoMobile = ({
  track,
  totalStreams,
  totalEarned,
  release,
  contributors,
  otherArtists,
  onShareButtonClick,
}: ReleasedTrackInfoProps & DataProps) => {
  const { t } = useTranslation('tracks');
  const isCurrentUser = useIsCurrentUser(track.user.id);
  const [hovering, setHovering] = useState(false);

  const getNonLiveTracksData = () => (
    <>
      <Row>
        <Col xs={12} className="d-flex justify-content-center">
          <div>
            <StyledButtonProStreamNowForDisabled disabled>
              <>
                <Play
                  style={{ marginRight: '10px' }}
                  size={14}
                  stroke="#284ed8"
                />
                {t('organisms:streamNow') as string}
              </>
            </StyledButtonProStreamNowForDisabled>
          </div>
        </Col>
        <Col xs={12}>
          <Col
            lg={5}
            className="d-flex justify-content-center mt-1"
            style={{ opacity: 0.45 }}
          >
            <>
              <TextBolder14 className="mr-1">
                {isCurrentUser ? (
                  <>{t('tracks:availableOn') as string}</>
                ) : (
                  <> {t('tracks:comingSoonOn') as string}</>
                )}
              </TextBolder14>
              <DistributionIconGroup
                iconSize="18"
                className="ml-1 align-items-center"
              />
            </>
          </Col>
        </Col>

        <Col xs={12} className="mt-1 text-center">
          <TextBold22>{track.title}</TextBold22>
        </Col>
        <Col xs={12} className="text-center">
          <TextRegular18 className="text-muted ">
            {track.user.display_name}
          </TextRegular18>
        </Col>
        <Col xs={12} style={{ marginTop: '0.5rem' }} className="text-center">
          <StatusForTrackDetailPage
            isCurrentUser={isCurrentUser}
            track={track}
          />
        </Col>
        <Col xs={12} className="d-flex justify-content-center mt-1">
          {isCurrentUser ? (
            <div
              className="d-flex flex-column"
              style={{ gap: '8px', marginTop: '10px' }}
            >
              <MobileThreeButtonGrid>
                <Button
                  disabled
                  outline
                  color="secondary"
                  className="d-flex align-items-center text-dark py-0 "
                  style={styleForNonLiveTracks}
                >
                  <>
                    <FiBarChart size={16} style={{ marginRight: '10px' }} />
                    {t('tracks:stats') as string}
                  </>
                </Button>
                <Button
                  disabled
                  outline
                  color="secondary"
                  className="d-flex align-items-center text-dark py-0 "
                  style={styleForNonLiveTracks}
                >
                  <>
                    <DollarSign size={14} style={{ marginRight: '10px' }} />
                    {t('organisms:earnings') as string}
                  </>
                </Button>
                <Button
                  disabled
                  outline
                  color="secondary"
                  className="d-flex align-items-center text-dark py-0 "
                  style={styleForNonLiveTracks}
                  onClick={onShareButtonClick}
                >
                  <>
                    <BiShareAlt size={16} style={{ marginRight: '10px' }} />
                    {t('tracks:share') as string}
                  </>
                </Button>
              </MobileThreeButtonGrid>
            </div>
          ) : (
            <div>
              <Button
                disabled
                outline
                color="secondary"
                className="d-flex align-items-center text-dark py-0 "
                style={styleForNonLiveTracks}
                onClick={onShareButtonClick}
              >
                <>
                  <BiShareAlt size={16} style={{ marginRight: '10px' }} />
                  {t('tracks:share') as string}
                </>
              </Button>
            </div>
          )}
        </Col>
        <Col lg={12} className="mt-2">
          <div className="d-flex flex-column">
            <GetTrackInfoOwnerData
              track={track}
              release={release}
              contributors={contributors}
              otherArtists={otherArtists}
            />
          </div>
        </Col>
      </Row>
    </>
  );

  const getLiveTracksData = () => (
    <>
      <Row>
        <Col xs={12} className="d-flex justify-content-center">
          <Link href={`/track-releases/${track.id}`}>
            <StyledButtonProStreamNow
              onMouseEnter={() => setHovering(true)}
              onMouseLeave={() => setHovering(false)}
            >
              <>
                {t('organisms:streamNow') as string}
                <Play
                  style={{ marginLeft: '10px' }}
                  size={14}
                  stroke={hovering ? '#fff' : '#284ed8'}
                />
              </>
            </StyledButtonProStreamNow>
          </Link>
        </Col>
        <Col xs={12}>
          <Col lg={5} className="d-flex justify-content-center mt-1">
            <>
              <TextBolder14 className="mr-1">
                {t('tracks:availableOn') as string}
              </TextBolder14>
              <DistributionIconGroup
                iconSize="18"
                className="ml-1 align-items-center"
              />
            </>
          </Col>
        </Col>

        <Col xs={12} className="mt-1 text-center">
          <TextBold22>{track.title}</TextBold22>
        </Col>
        <Col xs={12} className="text-center">
          <TextRegular18 className="text-muted">
            {track.user.display_name}
          </TextRegular18>
        </Col>
        <>
          {isCurrentUser ? (
            <Col
              xs={12}
              className="text-muted d-flex justify-content-center align-items-center mt-2 flex-column"
              style={{ gap: '8px' }}
            >
              <div className="d-flex align-items-center">
                <MdOutlinePlayArrow size={24} />
                <TextBold14 style={{ marginLeft: '0.3rem' }}>
                  {totalStreams} {t('tracks:overallStreams') as string}
                </TextBold14>
              </div>
              {totalEarned > 20 ? (
                <div className="d-flex align-items-center">
                  <BiDollar size={20} />
                  <TextBold14 style={{ marginLeft: '0.3rem' }}>
                    {totalEarned}$ {t('tracks:earned') as string}
                  </TextBold14>
                  <Link href="/statistics?tab=earnings">
                    <TextBold14 className="text-info ml-1 cursor-pointer">
                      {t('tracks:transferToWallet') as string}
                    </TextBold14>
                  </Link>
                </div>
              ) : (
                <></>
              )}
            </Col>
          ) : (
            <></>
          )}
        </>
        <Col xs={12} className="d-flex justify-content-center mt-2">
          {isCurrentUser ? (
            <div className="d-flex flex-column" style={{ gap: '8px' }}>
              <MobileTwoButtonGrid>
                <Link href={`/statistics?tab=releases&track_id=${track.id}`}>
                  <Button
                    outline
                    color="secondary"
                    className="d-flex align-items-center text-dark py-0 "
                    style={styleForNonLiveTracks}
                  >
                    <>
                      <FiBarChart size={16} style={{ marginRight: '10px' }} />
                      {t('tracks:stats') as string}
                    </>
                  </Button>
                </Link>

                <Button
                  outline
                  color="secondary"
                  className="d-flex align-items-center text-dark py-0 "
                  style={styleForNonLiveTracks}
                  onClick={onShareButtonClick}
                >
                  <>
                    <BiShareAlt size={16} style={{ marginRight: '10px' }} />
                    {t('tracks:share') as string}
                  </>
                </Button>
              </MobileTwoButtonGrid>
            </div>
          ) : (
            <div>
              <Button
                outline
                color="secondary"
                className="d-flex align-items-center text-dark py-0 "
                style={styleForNonLiveTracks}
                onClick={onShareButtonClick}
              >
                <>
                  <BiShareAlt size={16} style={{ marginRight: '10px' }} />
                  {t('tracks:share') as string}
                </>
              </Button>
            </div>
          )}
        </Col>
        <Col lg={12} className="mt-2">
          <GetTrackInfoOwnerData
            track={track}
            release={release}
            contributors={contributors}
            otherArtists={otherArtists}
          />
        </Col>
      </Row>
    </>
  );
  return (
    <>
      {track.release_status === 'live'
        ? getLiveTracksData()
        : getNonLiveTracksData()}
    </>
  );
};

export default ReleasedTrackInfoMobile;

interface ReleasedTrackInfoProps {
  track: Track;
}

const styleForNonLiveTracks = {
  padding: '4px 15px',
  height: '37px',
  minHeight: '10px',
  borderRadius: '5px',
  fontSize: '13px',
};
interface DataProps {
  totalStreams: number;
  totalEarned: number;
  onShareButtonClick: () => void;
  release: TrackRelease;
  contributors: TrackArtist[];
  otherArtists: TrackArtist[];
}
const StyledButtonProStreamNowForDisabled = styled.button`
  background: ${style.distributionGradient};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  white-space: nowrap;
  box-shadow: none;

  padding: 4px 15px;
  font-size: 13px;
  height: 37px;
  min-height: 10px;

  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 0;
  text-decoration: none;
  border-radius: 5px;

  :before {
    content: '';
    position: absolute;
    z-index: -1;
    inset: 0;
    border: 2px solid transparent;
    border-radius: 5px;
    background: inherit;
    background-origin: border-box;
    background-clip: border-box;
    -webkit-mask: linear-gradient(#fff 0 0) padding-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    -webkit-mask-repeat: no-repeat;
  }
`;

const MobileThreeButtonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 8px;
`;

const StyledButtonProStreamNow = styled(ProOutlineButton)`
  padding: 4px 15px !important;
  font-size: 13px !important;
  height: 35px !important;
  min-height: 10px !important;
`;
const MobileTwoButtonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 8px;
`;
